/**
 * ContractActionDialogs Component
 * Contains all dialog modals for contract workflow actions.
 * Approve/Reject are direct actions. Terminate/Rollover go through approval workflow.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Paper,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';
import { format } from 'date-fns';
import { apiPost, apiGet } from '../../../../../utils/apiClient';
import { formatNumber } from '../../../../../utils/validation';
import ContractActionDialogBase from '../../../ContractActionDialogBase';
import RolloverDialog from '../../../RolloverDialog';

function ContractActionDialogs({
  contractId,
  contract,
  approveDialogOpen,
  setApproveDialogOpen,
  rejectDialogOpen,
  setRejectDialogOpen,
  terminateDialogOpen,
  setTerminateDialogOpen,
  rolloverDialogOpen,
  setRolloverDialogOpen,
  onActionComplete,
}) {
  // Approve dialog state
  const [approveComments, setApproveComments] = useState('');

  // Reject dialog state
  const [rejectReason, setRejectReason] = useState('');

  // Termination dialog state
  const [terminationData, setTerminationData] = useState({
    termination_date: format(new Date(), 'yyyy-MM-dd'),
    settlement_amount: '',
    penalty_amount: '',
  });
  const [terminationReason, setTerminationReason] = useState('');
  const [terminationSubmitting, setTerminationSubmitting] = useState(false);
  const [terminationError, setTerminationError] = useState(null);
  const [settlementCalc, setSettlementCalc] = useState(null);
  const [calculatingSettlement, setCalculatingSettlement] = useState(false);

  // Calculate settlement when termination dialog opens or date changes
  useEffect(() => {
    if (terminateDialogOpen && terminationData.termination_date) {
      calculateSettlement(terminationData.termination_date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- calculateSettlement defined below; termination_date managed by change handler
  }, [terminateDialogOpen]);

  // Reset termination state on open
  useEffect(() => {
    if (terminateDialogOpen) {
      setTerminationData({
        termination_date: format(new Date(), 'yyyy-MM-dd'),
        settlement_amount: '',
        penalty_amount: '',
      });
      setTerminationReason('');
      setTerminationError(null);
      setSettlementCalc(null);
    }
  }, [terminateDialogOpen]);

  const calculateSettlement = async (terminationDate) => {
    if (!terminationDate || !contractId) return;
    setCalculatingSettlement(true);
    try {
      const response = await apiGet(`/contracts/${contractId}/calculate-settlement?termination_date=${terminationDate}`);
      if (response?.data) {
        setSettlementCalc(response.data);
        setTerminationData(prev => ({
          ...prev,
          settlement_amount: response.data.total_settlement.toString(),
        }));
      }
    } catch (err) {
      console.error('Error calculating settlement:', err);
      setSettlementCalc(null);
    } finally {
      setCalculatingSettlement(false);
    }
  };

  const handleTerminationDateChange = (newDate) => {
    setTerminationData(prev => ({ ...prev, termination_date: newDate }));
    calculateSettlement(newDate);
  };

  const handleApprove = async () => {
    try {
      await apiPost(`/contracts/${contractId}/approve`, { comments: approveComments });
      setApproveDialogOpen(false);
      setApproveComments('');
      window.location.reload();
    } catch (err) {
      console.error('Error approving contract:', err);
      alert('Failed to approve contract: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleReject = async () => {
    try {
      await apiPost(`/contracts/${contractId}/reject`, { reason: rejectReason });
      setRejectDialogOpen(false);
      setRejectReason('');
      window.location.reload();
    } catch (err) {
      console.error('Error rejecting contract:', err);
      alert('Failed to reject contract: ' + (err.response?.data?.message || err.message));
    }
  };

  // Early Termination → now goes through approval workflow
  const handleEarlyTermination = async ({ proof_document_ids }) => {
    setTerminationSubmitting(true);
    setTerminationError(null);
    try {
      const response = await apiPost(`/contracts/${contractId}/early-termination`, {
        termination_date: terminationData.termination_date,
        reason: terminationReason,
        settlement_amount: terminationData.settlement_amount ? parseFloat(terminationData.settlement_amount) : null,
        penalty_amount: terminationData.penalty_amount ? parseFloat(terminationData.penalty_amount) : null,
        proof_document_ids,
      });
      if (response?.success) {
        setTerminateDialogOpen(false);
        window.location.reload();
      } else {
        setTerminationError(response?.message || 'Failed to submit termination request');
      }
    } catch (err) {
      setTerminationError(err.response?.data?.message || err.message || 'Failed to submit termination request');
    } finally {
      setTerminationSubmitting(false);
    }
  };

  const currencyCode = contract?.currency_code || 'ZAR';

  // Settlement preview content for termination dialog
  const terminationPreviewContent = settlementCalc ? (
    <Box>
      <Typography variant="subtitle2" gutterBottom>Settlement Breakdown</Typography>
      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Grid container spacing={1}>
          <Grid size={8}>
            <Typography variant="body2" color="textSecondary">Original Capital:</Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="body2" align="right">{currencyCode} {formatNumber(settlementCalc.original_capital)}</Typography>
          </Grid>
          {settlementCalc.total_injections > 0 && (
            <>
              <Grid size={8}><Typography variant="body2" color="textSecondary" sx={{ pl: 1 }}>+ Capital Injections:</Typography></Grid>
              <Grid size={4}><Typography variant="body2" align="right" color="success.main">{currencyCode} {formatNumber(settlementCalc.total_injections)}</Typography></Grid>
            </>
          )}
          {settlementCalc.total_withdrawals > 0 && (
            <>
              <Grid size={8}><Typography variant="body2" color="textSecondary" sx={{ pl: 1 }}>- Capital Withdrawals:</Typography></Grid>
              <Grid size={4}><Typography variant="body2" align="right" color="warning.main">({currencyCode} {formatNumber(settlementCalc.total_withdrawals)})</Typography></Grid>
            </>
          )}
          {settlementCalc.total_capital_paid > 0 && (
            <>
              <Grid size={8}><Typography variant="body2" color="textSecondary" sx={{ pl: 1 }}>- Capital Paid:</Typography></Grid>
              <Grid size={4}><Typography variant="body2" align="right">({currencyCode} {formatNumber(settlementCalc.total_capital_paid)})</Typography></Grid>
            </>
          )}
          <Grid size={8}><Typography variant="body2" fontWeight="medium">Outstanding Principal:</Typography></Grid>
          <Grid size={4}><Typography variant="body2" align="right" fontWeight="medium">{currencyCode} {formatNumber(settlementCalc.outstanding_principal)}</Typography></Grid>
          <Grid size={8}><Typography variant="body2" color="textSecondary">Accrued Interest:</Typography></Grid>
          <Grid size={4}><Typography variant="body2" align="right">{currencyCode} {formatNumber(settlementCalc.accrued_interest)}</Typography></Grid>
          <Grid size={12}><Divider sx={{ my: 0.5 }} /></Grid>
          <Grid size={8}><Typography variant="body2" fontWeight="bold">Total Settlement:</Typography></Grid>
          <Grid size={4}><Typography variant="body2" fontWeight="bold" align="right">{currencyCode} {formatNumber(settlementCalc.total_settlement)}</Typography></Grid>
        </Grid>
      </Paper>
    </Box>
  ) : calculatingSettlement ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
      <CircularProgress size={20} />
      <Typography variant="body2" color="textSecondary">Calculating settlement...</Typography>
    </Box>
  ) : null;

  return (
    <>
      {/* Approve Dialog (direct action, not approval workflow) */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle><Typography variant="h6" fontWeight="bold">Approve Contract</Typography></DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to approve this contract. You may add optional comments below.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Comments (optional)"
            fullWidth
            multiline
            rows={3}
            value={approveComments}
            onChange={(e) => setApproveComments(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>
      {/* Reject Dialog (direct action, not approval workflow) */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)}>
        <DialogTitle><Typography variant="h6" fontWeight="bold">Reject Contract</Typography></DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for rejecting this contract:
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" disabled={!rejectReason}>
            Reject Contract
          </Button>
        </DialogActions>
      </Dialog>
      {/* Early Termination Dialog — uses shared base template + approval workflow */}
      <ContractActionDialogBase
        open={terminateDialogOpen}
        onClose={() => setTerminateDialogOpen(false)}
        contract={contract}
        workflowCode="contract_terminate"
        title="Early Termination"
        description="Terminate this contract early. This action will cancel any remaining scheduled payments. Settlement will be calculated based on the termination date."
        submitLabel="Submit for Approval"
        submitColor="error"
        isValid={!!terminationData.termination_date}
        submitting={terminationSubmitting}
        error={terminationError}
        onClearError={() => setTerminationError(null)}
        onSubmit={handleEarlyTermination}
        previewContent={terminationPreviewContent}
        reason={terminationReason}
        onReasonChange={setTerminationReason}
        reasonLabel="Termination Reason (Required)"
        reasonPlaceholder="Provide a detailed reason for early termination. This will be reviewed by an approver."
        maxWidth="lg"
      >
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              label="Termination Date"
              type="date"
              fullWidth
              value={terminationData.termination_date}
              onChange={(e) => handleTerminationDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Settlement will be calculated automatically"
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Settlement Amount"
              type="number"
              fullWidth
              value={terminationData.settlement_amount}
              onChange={(e) => setTerminationData(prev => ({ ...prev, settlement_amount: e.target.value }))}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>{currencyCode}</Typography> }}
              helperText="Auto-calculated, can be adjusted"
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Penalty Amount (Optional)"
              type="number"
              fullWidth
              value={terminationData.penalty_amount}
              onChange={(e) => setTerminationData(prev => ({ ...prev, penalty_amount: e.target.value }))}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>{currencyCode}</Typography> }}
              helperText="Early termination penalty if applicable"
            />
          </Grid>
        </Grid>
      </ContractActionDialogBase>
      {/* Rollover Dialog */}
      <RolloverDialog
        open={rolloverDialogOpen}
        onClose={() => setRolloverDialogOpen(false)}
        contract={contract}
        contractId={contractId}
        onSubmitSuccess={onActionComplete}
      />
    </>
  );
}

export default ContractActionDialogs;
